import React, {useState} from "react";
import "./NavBarEntry.css";
import {FormattedMessage} from "react-intl";
import {NavLink} from "react-router-dom";
import {Page} from "../views/LandingPage";

type NavBarEntryProps = {
  /** Unique identifier for the entry */
  id: string;
  /** Which page the link represents */
  page: Page;
  /** State tracking for which page the mouse last hovered over */
  lastHoveredPage: {get: Page; set: (p: Page) => void};
  /** Whether the page is currently being viewed and thus should be highlighted */
  isActive: boolean;
  /** Whether the currently hovered over page should be highlighted */
  hoverPrimary: boolean;
  /** Whether the nav is currently hidden (only relevant for mobile) */
  isNavHidden?: boolean;
  /** Method to execute on click on the entry – presences also indicates mobile styling */
  onMobileClick?: () => void;
  /** Method to execute on key down on the entry – not necessary on mobile */
  onKeyDown?: (keyCode: string, keyTyped: string) => void;
  /** Method to execute when the component receives focus without being clicked on */
  setKeyboardNavigating?: (primary: boolean) => void;
  /** Method to execute whenever a link is activated */
  setNextNavigationTarget: (newTarget: string) => void;
};

/**
 * Links to pages in a navigation sidebar/mobile menu.
 */
function NavBarEntry(props: NavBarEntryProps) {
  const [isMouseDown, setIsMouseDown] = useState(false);
  return (
    <NavLink
      key={"pageLink" + props.page.name}
      id={props.id}
      to={props.page.externalPage || props.page.name.toLowerCase()}
      target={props.page.externalPage ? "_blank" : undefined}
      rel={props.page.externalPage ? "noreferrer" : undefined}
      className={
        "navBarEntry" +
        (props.onMobileClick ? " navBarEntryButton" : "") +
        (props.isActive ? " active" : "") +
        (props.page.name === "Help" ? " navBarSecondaryLink" : "")
      }
      tabIndex={
        (props.page.name === "Home" && !props.onMobileClick) ||
        (props.onMobileClick && !props.isNavHidden)
          ? undefined
          : -1
      }
      onMouseEnter={() => {
        if (!props.onMobileClick && props.page !== undefined)
          props.lastHoveredPage.set(props.page);
      }}
      onFocus={() => {
        if (!props.onMobileClick && props.page !== undefined)
          props.lastHoveredPage.set(props.page);
        if (props.setKeyboardNavigating && !isMouseDown)
          props.setKeyboardNavigating(true);
      }}
      onMouseDown={() => setIsMouseDown(true)}
      onMouseUp={() => setIsMouseDown(false)}
      onClick={
        props.page.externalPage
          ? undefined
          : (e) => {
              props.setNextNavigationTarget(props.page.name.toLowerCase());
              e.preventDefault();
              if (props.onMobileClick) props.onMobileClick();
            }
      }
      onKeyDown={(e) =>
        props.onKeyDown ? props.onKeyDown(e.code, e.key) : null
      }
      aria-current={props.isActive ? "page" : undefined}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title />
        <use href={props.page.icon + "#globalG"} />
      </svg>
      <div
        className={
          props.isActive ||
          (props.lastHoveredPage.get.name === props.page.name &&
            props.hoverPrimary)
            ? "active"
            : ""
        }
      >
        {
          <FormattedMessage
            id={"landingPage.nav." + props.page.name.toLowerCase()}
          />
        }
      </div>
    </NavLink>
  );
}

export {NavBarEntry};
